import React, { useCallback, useEffect, useMemo, useState } from 'react';
import f from './Filters.module.css';
import { BranchGroupsAPI } from '../../../../api/BranchGroups';
import { BranchOfficeFetchAPI } from '../../../../api/BranchOffices';
import { toggleValue } from '../../utils/dashboardFilters';
import { isBranchLimitedUser } from '../../utils/branchGroupPermissions';

const readStorageJSON = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

const apiError = (error, fallback) => error?.response?.data?.error || error?.response?.data?.message || fallback;

const EMPTY_FORM = { id: null, name: '', codes: [] };

/* Administración de grupos de sucursales: listar, crear, editar y desactivar.
   Solo se abre para usuarios con la acción visible, pero cada petición la valida el servidor. */
export default function BranchGroupsModal({ businessCode, onClose, onChanged }) {
  const token = localStorage.getItem('x-access-token');
  const businessId = readStorageJSON('business')?.id ?? readStorageJSON('user')?.business_id;

  const [groups, setGroups] = useState([]);
  const [companyBranches, setCompanyBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [groupsResponse, branchesResponse] = await Promise.all([
        BranchGroupsAPI.getBranchGroups({ business_code: businessCode, status: 'all', token }),
        BranchOfficeFetchAPI.getBranchOfficesByBusiness(businessId, token),
      ]);
      setGroups(Array.isArray(groupsResponse?.data?.data) ? groupsResponse.data.data : []);
      const branches = Array.isArray(branchesResponse?.data?.data) ? branchesResponse.data.data : [];
      const all = branches.map((branch) => ({ code: String(branch.branchoffice_code), name: branch.branchofficeName }));
      // A user limited to their branches can only build groups from the ones assigned to them (the server enforces it too)
      const user = readStorageJSON('user');
      const assigned = new Set((Array.isArray(user?.branchoffices) ? user.branchoffices : []).map((branch) => String(branch.code || branch.branchoffice_code)));
      setCompanyBranches(isBranchLimitedUser(user) ? all.filter((branch) => assigned.has(branch.code)) : all);
    } catch (error) {
      setLoadError(apiError(error, 'No se pudieron cargar los grupos'));
    } finally {
      setLoading(false);
    }
  }, [businessCode, businessId, token]);

  useEffect(() => { load(); }, [load]);

  const branchNames = useMemo(() => new Map(companyBranches.map((branch) => [branch.code, branch.name])), [companyBranches]);

  const startCreate = () => { setActionError(null); setConfirmId(null); setForm({ ...EMPTY_FORM }); };
  const startEdit = (group) => { setActionError(null); setConfirmId(null); setForm({ id: group.id, name: group.name, codes: [...group.branchoffice_codes] }); };

  const runAction = async (action, fallbackMessage) => {
    setSaving(true);
    setActionError(null);
    try {
      await action();
      await load();
      if (onChanged) onChanged();
      return true;
    } catch (error) {
      setActionError(apiError(error, fallbackMessage));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const save = async (event) => {
    event.preventDefault();
    const name = form.name.trim();
    const ok = await runAction(
      () => (form.id
        ? BranchGroupsAPI.updateBranchGroup(form.id, { name, branchoffice_codes: form.codes }, token)
        : BranchGroupsAPI.newBranchGroup({ business_code: businessCode, name, branchoffice_codes: form.codes }, token)),
      'No se pudo guardar el grupo'
    );
    if (ok) setForm(null);
  };

  const deactivate = async (group) => {
    const ok = await runAction(() => BranchGroupsAPI.deactivateBranchGroup(group.id, token), 'No se pudo desactivar el grupo');
    if (ok) setConfirmId(null);
  };

  const reactivate = (group) => runAction(
    () => BranchGroupsAPI.updateBranchGroup(group.id, { status: 'active' }, token),
    'No se pudo reactivar el grupo'
  );

  const memberNames = (group) => group.branchoffice_codes
    .map((code) => branchNames.get(code) || group.branches?.find((branch) => branch.code === code)?.name || `${code} (ya no existe)`)
    .join(', ');

  return (
    <div className={f.overlay} role="presentation">
      <div className={f.modal} role="dialog" aria-modal="true" aria-label="Grupos de sucursales">
        <div className={f.modalHead}>
          <span className={f.modalTitle}>Grupos de sucursales</span>
          <button type="button" className={f.secondaryBtn} onClick={onClose}>Cerrar</button>
        </div>

        <div className={f.modalBody}>
          {actionError && <div className={f.error} role="alert">{actionError}</div>}
          {loading && <span className={f.hint} role="status">Cargando…</span>}
          {!loading && loadError && (
            <div className={f.error} role="alert">
              <span>{loadError}</span>
              <button type="button" className={f.linkBtn} onClick={load}>Reintentar</button>
            </div>
          )}

          {form && (
            <form onSubmit={save} className={f.formField} aria-label={form.id ? 'Editar grupo' : 'Nuevo grupo'}>
              <label className={f.formField}>
                <span className={f.label}>Nombre</span>
                <input
                  type="text"
                  value={form.name}
                  maxLength={80}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
              </label>
              <span className={f.label}>Sucursales ({form.codes.length})</span>
              <div className={f.branchList} role="group" aria-label="Sucursales del grupo">
                {companyBranches.map((branch) => (
                  <label key={branch.code} className={f.option}>
                    <input
                      type="checkbox"
                      checked={form.codes.includes(branch.code)}
                      onChange={() => setForm({ ...form, codes: toggleValue(form.codes, branch.code) })}
                    />
                    <span className={f.optionName}>{branch.name}</span>
                  </label>
                ))}
                {/* Codes saved in the group whose branch no longer exists stay visible so they are not lost silently */}
                {form.codes.filter((code) => !branchNames.has(code)).map((code) => (
                  <label key={code} className={f.option}>
                    <input type="checkbox" checked onChange={() => setForm({ ...form, codes: toggleValue(form.codes, code) })} />
                    <span className={f.optionName}>{code} (ya no existe)</span>
                  </label>
                ))}
              </div>
              <div className={f.modalFoot} style={{ padding: 0, border: 0 }}>
                <button type="button" className={f.secondaryBtn} onClick={() => setForm(null)} disabled={saving}>Cancelar</button>
                <button type="submit" className={f.primaryBtn} disabled={saving || !form.name.trim() || form.codes.length === 0}>
                  {form.id ? 'Guardar cambios' : 'Crear grupo'}
                </button>
              </div>
            </form>
          )}

          {!loading && !loadError && !form && (
            <>
              <div>
                <button type="button" className={f.primaryBtn} onClick={startCreate}>Nuevo grupo</button>
              </div>
              {groups.length === 0 && <span className={f.hint}>Aún no hay grupos de sucursales.</span>}
              {groups.map((group) => {
                const inactive = group.status === 'inactive';
                return (
                  <div key={group.id} className={`${f.groupRow} ${inactive ? f.groupRowInactive : ''}`} data-testid={`group-row-${group.id}`}>
                    <div>
                      <div className={f.groupName}>
                        {group.name}
                        {inactive && <span className={f.badge}>Inactivo</span>}
                      </div>
                      <div className={f.groupBranches}>
                        {group.branch_count} {group.branch_count === 1 ? 'sucursal' : 'sucursales'}: {memberNames(group)}
                      </div>
                    </div>
                    <div className={f.rowActions}>
                      {!inactive && confirmId !== group.id && (
                        <>
                          {group.can_edit !== false && (
                            <button type="button" className={f.secondaryBtn} onClick={() => startEdit(group)}>Editar</button>
                          )}
                          {group.can_deactivate !== false && (
                            <button type="button" className={f.secondaryBtn} onClick={() => { setActionError(null); setConfirmId(group.id); }}>Desactivar</button>
                          )}
                          {group.can_edit === false && group.can_deactivate === false && (
                            <span className={f.hint}>Solo lectura</span>
                          )}
                        </>
                      )}
                      {!inactive && confirmId === group.id && (
                        <div className={f.confirmRow}>
                          <span>¿Desactivar «{group.name}»? Se conserva su configuración.</span>
                          <button type="button" className={f.primaryBtn} disabled={saving} onClick={() => deactivate(group)}>Confirmar</button>
                          <button type="button" className={f.secondaryBtn} onClick={() => setConfirmId(null)}>Cancelar</button>
                        </div>
                      )}
                      {inactive && group.can_edit !== false && (
                        <button type="button" className={f.secondaryBtn} disabled={saving} onClick={() => reactivate(group)}>Reactivar</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
